import { validateNumeric2DArray, validateNumericArray, validateShapesMatch, validateFitted, validateNumber } from '../utils/validation.js';
import { transpose, matrixMultiply, matrixVectorMultiply, inverse, identity, addMatrices, scalarMultiply } from '../utils/matrix.js';

export class LinearRegression {
  constructor(options = {}) {
    this.fitIntercept = options.fitIntercept !== undefined ? options.fitIntercept : true;
    this.normalize = options.normalize || false;
    this.regularization = options.regularization || null;
    this.alpha = options.alpha || 1.0;
    
    this.coefficients = null;
    this.intercept = null;
    this.isFitted = false;
    this.featureMeans = null;
    this.featureStds = null;
  }

  fit(X, y) {
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }
    
    validateNumeric2DArray(X, 'X');
    validateNumericArray(y, 'y');
    validateShapesMatch(X, y);
    
    let XProcessed = X.map(row => [...row]);
    
    if (this.normalize) {
      const nFeatures = XProcessed[0].length;
      this.featureMeans = Array(nFeatures).fill(0);
      this.featureStds = Array(nFeatures).fill(0);
      
      for (let j = 0; j < nFeatures; j++) {
        const column = XProcessed.map(row => row[j]);
        this.featureMeans[j] = column.reduce((a, b) => a + b, 0) / column.length;
        
        const variance = column.reduce((sum, val) => 
          sum + Math.pow(val - this.featureMeans[j], 2), 0) / column.length;
        this.featureStds[j] = Math.sqrt(variance) || 1;
        
        for (let i = 0; i < XProcessed.length; i++) {
          XProcessed[i][j] = (XProcessed[i][j] - this.featureMeans[j]) / this.featureStds[j];
        }
      }
    }
    
    if (this.fitIntercept) {
      XProcessed = XProcessed.map(row => [1, ...row]);
    }
    
    const XT = transpose(XProcessed);
    const XTX = matrixMultiply(XT, XProcessed);
    
    if (this.regularization === 'ridge') {
      const nFeatures = XTX.length;
      const regularizationMatrix = scalarMultiply(identity(nFeatures), this.alpha);
      
      if (this.fitIntercept) {
        regularizationMatrix[0][0] = 0;
      }
      
      const XTXReg = addMatrices(XTX, regularizationMatrix);
      const XTXInv = inverse(XTXReg);
      const XTy = matrixVectorMultiply(XT, y);
      this.coefficients = matrixVectorMultiply(XTXInv, XTy);
    } else {
      const XTXInv = inverse(XTX, { regularization: 1e-10 });
      const XTy = matrixVectorMultiply(XT, y);
      this.coefficients = matrixVectorMultiply(XTXInv, XTy);
    }
    
    if (this.fitIntercept) {
      this.intercept = this.coefficients[0];
      this.coefficients = this.coefficients.slice(1);
    } else {
      this.intercept = 0;
    }
    
    this.isFitted = true;
    return this;
  }

  predict(X) {
    validateFitted(this.isFitted, 'LinearRegression');
    
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }
    
    validateNumeric2DArray(X, 'X');
    
    let XProcessed = X.map(row => [...row]);
    
    if (this.normalize) {
      for (let j = 0; j < XProcessed[0].length; j++) {
        for (let i = 0; i < XProcessed.length; i++) {
          XProcessed[i][j] = (XProcessed[i][j] - this.featureMeans[j]) / this.featureStds[j];
        }
      }
    }
    
    return XProcessed.map(row => {
      let prediction = this.intercept;
      for (let i = 0; i < row.length; i++) {
        prediction += row[i] * this.coefficients[i];
      }
      return prediction;
    });
  }

  score(X, y) {
    validateFitted(this.isFitted, 'LinearRegression');
    
    const predictions = this.predict(X);
    const yMean = y.reduce((sum, val) => sum + val, 0) / y.length;
    
    let ssRes = 0;
    let ssTot = 0;
    
    for (let i = 0; i < y.length; i++) {
      ssRes += Math.pow(y[i] - predictions[i], 2);
      ssTot += Math.pow(y[i] - yMean, 2);
    }
    
    return 1 - (ssRes / ssTot);
  }
}

export class RidgeRegression extends LinearRegression {
  constructor(options = {}) {
    super({ ...options, regularization: 'ridge' });
  }
}

export class LassoRegression extends LinearRegression {
  constructor(options = {}) {
    super(options);
    this.regularization = 'lasso';
    this.maxIterations = options.maxIterations || 1000;
    this.tolerance = options.tolerance || 0.0001;
  }

  fit(X, y) {
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }
    
    validateNumeric2DArray(X, 'X');
    validateNumericArray(y, 'y');
    validateShapesMatch(X, y);
    
    let XProcessed = X.map(row => [...row]);
    
    if (this.normalize) {
      const nFeatures = XProcessed[0].length;
      this.featureMeans = Array(nFeatures).fill(0);
      this.featureStds = Array(nFeatures).fill(0);
      
      for (let j = 0; j < nFeatures; j++) {
        const column = XProcessed.map(row => row[j]);
        this.featureMeans[j] = column.reduce((a, b) => a + b, 0) / column.length;
        
        const variance = column.reduce((sum, val) => 
          sum + Math.pow(val - this.featureMeans[j], 2), 0) / column.length;
        this.featureStds[j] = Math.sqrt(variance) || 1;
        
        for (let i = 0; i < XProcessed.length; i++) {
          XProcessed[i][j] = (XProcessed[i][j] - this.featureMeans[j]) / this.featureStds[j];
        }
      }
    }
    
    const nSamples = XProcessed.length;
    const nFeatures = XProcessed[0].length;
    
    this.coefficients = Array(nFeatures).fill(0);
    this.intercept = 0;
    
    for (let iter = 0; iter < this.maxIterations; iter++) {
      const oldCoefficients = [...this.coefficients];
      const oldIntercept = this.intercept;
      
      if (this.fitIntercept) {
        let interceptSum = 0;
        for (let i = 0; i < nSamples; i++) {
          let prediction = 0;
          for (let k = 0; k < nFeatures; k++) {
            prediction += XProcessed[i][k] * this.coefficients[k];
          }
          interceptSum += (y[i] - prediction);
        }
        this.intercept = interceptSum / nSamples;
      }
      
      for (let j = 0; j < nFeatures; j++) {
        let rho = 0;
        let norm = 0;
        
        for (let i = 0; i < nSamples; i++) {
          let prediction = this.intercept;
          for (let k = 0; k < nFeatures; k++) {
            if (k !== j) {
              prediction += XProcessed[i][k] * this.coefficients[k];
            }
          }
          rho += XProcessed[i][j] * (y[i] - prediction);
          norm += XProcessed[i][j] * XProcessed[i][j];
        }
        
        if (norm === 0) {
          this.coefficients[j] = 0;
        } else if (rho < -this.alpha) {
          this.coefficients[j] = (rho + this.alpha) / norm;
        } else if (rho > this.alpha) {
          this.coefficients[j] = (rho - this.alpha) / norm;
        } else {
          this.coefficients[j] = 0;
        }
      }
      
      const coeffDiff = oldCoefficients.reduce((sum, val, i) => 
        sum + Math.abs(val - this.coefficients[i]), 0);
      const interceptDiff = Math.abs(oldIntercept - this.intercept);
      
      if (coeffDiff + interceptDiff < this.tolerance) {
        break;
      }
    }
    
    this.isFitted = true;
    return this;
  }
}
